import { useCallback, useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import {
  createPackageItem,
  deletePackageItem,
  fetchPackageItems,
  patchPackageItem,
} from "api/packages"
import { css, cx } from "@emotion/css"
import {
  marketsClass,
  openMarkets,
  subRegionClass,
  summaryClass,
  summaryMarkers,
  summaryTitle,
} from "./css"
import { queryCache, useMutation, useQuery } from "react-query"

import { ApiError } from "api/http/utils"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { IErrors } from "."
import MoreActions from "components/MoreActions"
import Options from "components/inputs/Options"
import { PackageItem } from "models/packages"
import { Rable } from "components/Rable"
import { columns } from "./columns"
import { fetchSupplierProducts } from "api/suppliers"
import { useAuthenticated } from "providers/A12n"
import { useHistory } from "react-router"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"
import { useToggle } from "hooks/useToggle"

interface IPackageItemProps {
  item: PackageItem
  supplierId: string
  packageId: string
  open: string[]
  handleOpenSubmedias: (ids: string[]) => void
  submediaTypesSemantics: any[]
  activateErrors?: IErrors
  isActive: boolean
  mediaTypeSemantic: string
  clearErrors: (rowId: string, name: string) => void
}

function SubMedia(props: IPackageItemProps) {
  const { t } = useI18n()
  const history = useHistory()
  const feedback = useSendFeedBack()
  const {
    item,
    supplierId,
    packageId,
    submediaTypesSemantics,
    isActive,
    mediaTypeSemantic,
    clearErrors,
    handleOpenSubmedias,
    open,
  } = props
  const [subMediaType, setSubMediaType] = useState<string | undefined>(
    item.mediaSubtypeSemanticCode,
  )

  const { hasAuthority } = useAuthenticated()
  const editAllowed = hasAuthority(["AUTH_EDIT_RATE_CARD"]) && !isActive
  const actionAllowed = hasAuthority(["AUTH_DELETE_RATE_CARD"]) && !isActive

  const [onEdit, setOnEdit] = useState(false)

  const [subMediaErrors, setSubMediaErrors] = useState<string[] | undefined>(
    undefined,
  )
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const isModalOpen = Boolean(modalInfo)
  const [subRgModalInfo, setSubRgModalInfo] = useState<ModalInfo>()
  const isSubRgModalOpen = Boolean(subRgModalInfo)
  ////////////////////////////////////////////////
  const { data: items, status } = useQuery(
    ["package-items", packageId, ""],
    fetchPackageItems,
  )

  const isPending = status === "loading"

  const searchParams = new URLSearchParams()
  searchParams.append("mediaTypeSemanticCodes", mediaTypeSemantic)

  const { data: vendorProducts } = useQuery(
    ["market-vendors-products", supplierId, searchParams.toString()],
    fetchSupplierProducts,
    { enabled: Boolean(supplierId) },
  )
  const vendorProductsMapped =
    vendorProducts?.map((s) => ({
      label: s.name,
      value: s.id,
    })) || []

  ////////////////////////////////////////////////

  const newItemDisable = () => (row: Partial<PackageItem>) =>
    !row?.name || !row?.supplierProductId

  const [_delete] = useMutation(deletePackageItem, {
    onSuccess: () => {
      setSubRgModalInfo(undefined)
      queryCache.invalidateQueries(["package-items"])
    },
  })
  const [update] = useMutation(patchPackageItem, {
    onSuccess: () => {
      setOnEdit(false)
      setSubMediaErrors(undefined)

      //  setTableErrors(undefined)
      queryCache.invalidateQueries("package-items")
      history.push(`/packages/${packageId}`)
    },
    onError: (error: ApiError) => {
      setSubMediaErrors(error.fields?.map((e: any) => e.key))
    },
  })

  const [remove] = useMutation(deletePackageItem, {
    onSuccess: (_, id: string) => {
      setModalInfo(undefined)
      //   setTableErrors(undefined)
      const ids = open?.filter((s) => s !== item.id)

      handleOpenSubmedias(ids)
      queryCache.invalidateQueries("package-items")
    },
    onError: (e) => {
      setModalInfo(undefined)
      // setTableErrors([e.message])
    },
  })

  const [patch] = useMutation(patchPackageItem, {
    onSuccess: () => {
      queryCache.invalidateQueries("package-items")
    },
    onError: (e: ApiError) => {
      feedback(t(e?.message), "error")
    },
  })

  const handleDelete = useCallback(
    (rowId: string) => {
      setModalInfo({
        title: t("lineitem.modal.delete.title"),
        type: "primary_destructive",
        description: t("lineitem.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: async () => await remove(rowId || ""),
      })
    },
    [remove, t],
  )

  const handleSubMediaDelete = useCallback(
    (id: string) => {
      setSubRgModalInfo({
        title: t("submedia.modal.delete.title"),
        type: "primary_destructive",
        description: t("submedia.modal.delete.description"),
        submitLabel: t("modal.label.delete"),
        onSubmit: () => _delete(id),
      })
    },
    [_delete, t],
  )

  const handleToggle = () => {
    const ids = !!isSubmediaOpen
      ? open?.filter((s) => s !== item.id)
      : [...open, item?.id]

    handleOpenSubmedias(ids)
  }

  const onUpdate = useCallback(
    (rowId: string, row: Partial<PackageItem>) => {
      patch({
        id: rowId,
        type: "line_item",
        ...row,
      })
    },
    [patch],
  )
  const [create] = useMutation(createPackageItem, {
    onSuccess: (response: any) => {
      /*    state.addRow(response) */
      handleOpenSubmedias([...open, item.id])
      // setErrors(undefined)
      queryCache.invalidateQueries(["package-items"])
    },
    onError: (error: ApiError) => {
      //  setErrors(error.fields?.map((e: any) => e))
    },
  })
  const onCreate = (newRow: Partial<PackageItem>) => {
    return create({
      ...newRow,
      type: "line_item",
      parentId: item.id,
      id: packageId,
    })
  }

  const newRowCols = columns(vendorProductsMapped, editAllowed)?.filter(
    (c) => c.existsInCreate,
  )
  const newRowToggle = useToggle()

  const required = columns()?.filter((c) => c.required)

  const newItems = items?.filter(
    (lineItem: Partial<PackageItem>) =>
      lineItem.type === "line_item" &&
      lineItem.parentId === item.id &&
      lineItem,
  )

  const getError = (id: string, name: string) => {
    if (
      props.activateErrors?.tableErrors &&
      props.activateErrors.tableErrors.some(
        (e) => e.rowId === id && e.name === name,
      )
    )
      return name
    else return undefined
  }

  const isSubmediaOpen = open?.includes(item.id || "")

  return (
    <div className={subRegionClass}>
      <div
        className={cx(summaryClass, {
          [styles.selectedMediaTypes]: isSubmediaOpen,
        })}
      >
        <div
          className={summaryTitle}
          onClick={() => editAllowed && setOnEdit(true)}
        >
          {onEdit ? (
            <Options
              onChange={(e) => {
                setSubMediaType(e.target.value)
                setSubMediaErrors(undefined)
                item.id &&
                  update({
                    mediaSubtypeSemanticCode: e.target.value,
                    id: item.id,
                    type: "media_subtype_plan",
                  })
              }}
              placeholder={"Select Submedia Type..."}
              name="mediaSubtypeSemanticCode"
              label={t("packages.submedia.type")}
              options={submediaTypesSemantics}
              value={subMediaType}
              size="small"
              onBlur={() => setOnEdit(false)}
              className={styles.subMediaOptions}
            />
          ) : (
            item.mediaSubtypeSemanticName
          )}
        </div>

        <div style={{ display: "flex" }}>
          <div onClick={handleToggle} className={summaryMarkers}>
            <strong>
              {isSubmediaOpen ? "Hide Line Items" : "View Line Items"}
            </strong>
          </div>
          {!isActive ? (
            <MoreActions
              options={[
                // DELETE
                {
                  label: t("subregions.list.delete.action"),
                  icon: <DeleteIcon />,
                  onClick: () => {
                    item.id && handleSubMediaDelete(item.id)
                  },
                },
              ]}
            />
          ) : (
            <div className={emptyDiv} />
          )}
        </div>
      </div>

      <div className={cx(marketsClass, { [openMarkets]: isSubmediaOpen })}>
        {(newItems || [])?.length > 0 && (
          <div className={createMarkets}>
            {newItems?.length}
            &nbsp;
            {"Line Items"}
            {!isActive && (
              <strong
                className={cx(createNew, { [toggle]: newRowToggle.open })}
                onClick={newRowToggle.onOpen}
              >
                {"+ New Line Item"}
              </strong>
            )}
          </div>
        )}
        <Rable<Partial<PackageItem>>
          getError={getError}
          data={newItems || []}
          required={required}
          columns={columns(vendorProductsMapped, editAllowed, clearErrors)}
          createRow={onCreate}
          newRowColumns={newRowCols}
          updateRow={onUpdate}
          label="rate.card.loading"
          open={newRowToggle.open}
          onOpen={newRowToggle.onOpen}
          onClose={newRowToggle.onClose}
          hasActions={actionAllowed}
          actions={(row: Partial<PackageItem>) => [
            {
              label: t("actions.delete"),
              hidden: false,
              onClick: () => handleDelete(row?.id || ""),
              labelStyle: styles.deleteLabel,
            },
          ]}
          disabledCondition={newItemDisable()}
          pending={isPending}
          emptyState={
            !newItems?.length &&
            !newRowToggle.open && (
              <>
                <div className={emptyMessage}>
                  {t("package.empty.lineItem")}
                </div>
                {!isActive ? (
                  <div onClick={newRowToggle.onOpen} className={emptyMarket}>
                    {t("package.empty.add.lineItem")}
                  </div>
                ) : null}
              </>
            )
          }
        />
      </div>
      <div className={errorMessage}>
        {subMediaErrors ? subMediaErrors.map((error: any) => t(error)) : ""}
      </div>
      <ConfirmationModal
        open={isModalOpen}
        onCancel={() => setModalInfo(undefined)}
        {...modalInfo}
      />
      <ConfirmationModal
        open={isSubRgModalOpen}
        onCancel={() => setSubRgModalInfo(undefined)}
        {...subRgModalInfo}
      />
    </div>
  )
}

export default SubMedia

const styles = {
  header: css`
    display: flex;
    justify-content: space-between;
    padding: 11px 103px 11px 43px;
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }

    .RebaseTable__cell.RebaseTable__grid-cell {
      display: flex;
      justify-content: center;
    }

    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }

      :last-child {
        display: flex;
        justify-content: center;
      }
    }

    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  selectedMediaTypes: css`
    border-radius: 4px;
    border: solid 1px var(--primary-main);
    background-color: var(--white);
  `,
  subMediaOptions: css`
    width: 233px;
  `,
  options: css`
    width: 150px;
    font-size: 14px;
  `,
  inputs: css`
    align-items: center;
    width: 989px;
    height: 40px;
    display: flex;
  `,
  deleteLabel: css`
    > span {
      color: var(--red);
    }
  `,
}

const errorMessage = css`
  font-size: 13px;
  color: var(--light-red);
  white-space: pre-wrap;
`
const emptyMessage = css`
  margin: 27px 0px 8px 0px;
  font-size: 16px;
  font-weight: 300;
  text-align: center;
  color: var(--dark-grey);
`

const emptyMarket = css`
  font-size: 13px;
  font-weight: bold;
  text-align: center;
  color: var(--primary-main);
  cursor: pointer;
`

const createNew = css`
  font-size: 13px;
  color: var(--primary-main);
  margin-left: 35px;
  cursor: pointer;
`

const createMarkets = css`
  font-size: 13px;
  margin-bottom: 20px;
  margin-top: 20px;
`

const toggle = css`
  display: none;
`

const emptyDiv = css`
  width: 42px;
`
